import { useCallback, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import type { Trace } from '@engine/models'

import { AdminListLayout } from '#components/AdminListLayout.js'
import { Slider } from '#components/Slider'
import { useInfiniteAdminResource } from '#hooks/useInfiniteAdminResource'
import { useIntersectionObserver } from '#hooks/useIntersectionObserver'

import { TraceDetails } from './TraceDetails'
import { TracesFilterForm } from './TracesFilterForm'
import { TracesTable } from './TracesTable'
import {
  isScoped,
  parseTracesFilters,
  type TracesFilters,
  toSearchParams,
  toTracesQuery,
} from './tracesFilters'

export const TracesView = () => {
  // The URL is the source of truth for filters, so views can be shared and bookmarked
  const [searchParams, setSearchParams] = useSearchParams()
  const filters = useMemo(
    () => parseTracesFilters(searchParams),
    [searchParams],
  )
  const query = useMemo(() => toTracesQuery(filters), [filters])
  const canQuery = isScoped(filters)

  const [selectedTrace, setSelectedTrace] = useState<Trace | undefined>()

  const {
    items,
    error,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    refresh,
    fetchNextPage,
    total,
  } = useInfiniteAdminResource<Trace>('traces', query, { enabled: canQuery })

  const handleLoadMore = useCallback(() => {
    if (!isFetchingNextPage && hasNextPage) {
      fetchNextPage()
    }
  }, [isFetchingNextPage, hasNextPage, fetchNextPage])

  const sentinelRef = useIntersectionObserver(handleLoadMore, {
    enabled: hasNextPage && !isFetchingNextPage,
    threshold: 0.1,
  })

  const handleFiltersChange = (value: TracesFilters) => {
    setSearchParams(toSearchParams(value), { replace: true })
    setSelectedTrace(undefined)
  }

  return (
    <>
      <AdminListLayout
        title="Traces"
        isLoading={isLoading}
        error={error}
        onRefresh={refresh}
        emptyMessage={
          canQuery
            ? 'No traces found'
            : 'Choose a SCAC or layer to use the advanced filter'
        }
        filters={
          <TracesFilterForm
            value={filters}
            onChange={handleFiltersChange}
            isLoading={isLoading}
            onRefresh={refresh}
          />
        }
        listContent={
          items.length > 0 ? (
            <div className="flex flex-col h-full gap-4">
              <div className="text-sm opacity-70">
                {total?.toLocaleString() ?? items.length} traces
              </div>
              <TracesTable
                traces={items}
                selectedTraceId={selectedTrace?.traceId}
                onSelect={setSelectedTrace}
                infiniteScrollRef={sentinelRef}
                isFetchingMore={isFetchingNextPage}
              />
            </div>
          ) : null
        }
      />

      <Slider
        isOpen={!!selectedTrace}
        onClose={() => setSelectedTrace(undefined)}
        title="Trace Details"
      >
        {selectedTrace && <TraceDetails trace={selectedTrace} />}
      </Slider>
    </>
  )
}
